import { useState, useMemo, useEffect } from 'react';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Plus, Pencil, Trash2, BookOpen, Sparkles,
  PowerOff, RotateCcw,
  LayoutGrid, List, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { RowActionMenu } from '../../components/ui/RowActionMenu';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { AssistenteBNCC } from '../../components/materias/AssistenteBNCC';
import { hasPermission } from '../../auth/permissions';
import type { Materia, Usuario, Turma } from '../../types/models';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { MateriasFormModal } from '../../components/forms/MateriasFormModal';
import '../../components/ui/datatable.css';
import './materias-modal.css';
import { ROUTES } from '../../api/routes';

export function MateriasPage() {
  const { canManage: canManageFn } = usePermissions();
  const canManage = canManageFn('materias');

  const user = useAuthStore(s => s.user);
  const escolaId = user?.escolaId;
  const { data: materias, isLoading, error: loadError, create, update, deactivate } = useCrud<Materia>('materias');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Materia | null>(null);
  const [assistenteOpen, setAssistenteOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    mat: Materia;
    title: string;
    message: string;
    confirmLabel: string;
    variant: 'danger' | 'default';
  } | null>(null);
  const [form, setForm] = useState({
    nome: '', codigo: '', areaDoCOnhecimento: '', siglaBNCC: '',
    corVisual: '#3b82f6', obrigatoria: false,
    etapasAplicaveis: [] as string[],
  });

  // Carrega professores e turmas para o assistente
  const { data: professores = [] } = useQuery<Usuario[]>({
    queryKey: ['professores', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.professores.listar(escolaId!));
      return data;
    },
    enabled: !!escolaId,
  });

  const { data: turmas = [] } = useQuery<Turma[]>({
    queryKey: ['turmas', escolaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.turmas.listar(escolaId!));
      return data;
    },
    enabled: !!escolaId,
  });

  // Séries únicas extraídas das turmas (para Etapa 2 do wizard)
  const seriesDisponíveis = useMemo(() => {
    const s = new Set(turmas.map((t) => t.serie));
    return Array.from(s).sort();
  }, [turmas]);

  const openNew = () => {
    setEditing(null);
    setForm({ nome: '', codigo: '', areaDoCOnhecimento: '', siglaBNCC: '', corVisual: '#3b82f6', obrigatoria: false, etapasAplicaveis: [] });
    setModalOpen(true);
  };

  const openEdit = (m: Materia) => {
    setEditing(m);
    setForm({
      nome: m.nome,
      codigo: m.codigo || '',
      areaDoCOnhecimento: m.areaDoCOnhecimento || '',
      siglaBNCC: m.siglaBNCC || '',
      corVisual: m.corVisual || '#3b82f6',
      obrigatoria: m.obrigatoria || false,
      etapasAplicaveis: m.etapasAplicaveis || [],
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        nome: form.nome,
        codigo: form.codigo || undefined,
        areaDoCOnhecimento: form.areaDoCOnhecimento || undefined,
        siglaBNCC: form.siglaBNCC || undefined,
        corVisual: form.corVisual || undefined,
        obrigatoria: form.obrigatoria,
        etapasAplicaveis: form.etapasAplicaveis,
      };
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload } as any);
        toast.success('Matéria atualizada!');
      } else {
        await create.mutateAsync(payload as any);
        toast.success('Matéria criada!');
      }
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      setModalOpen(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Erro ao salvar matéria';
      toast.error(msg);
    }
  };

  const formatUsoDetalhes = (detalhes: Record<string, number>) =>
    Object.entries(detalhes)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} ${k}`)
      .join(', ');

  /** Abre modal após GET /uso (mesma regra do backend: soft se ativa ou com vínculos; hard só inativa sem vínculos). */
  const requestDelete = async (mat: Materia) => {
    try {
      const { data: uso } = await api.get<{ emUso: boolean; detalhes: Record<string, number> }>(
        `/escolas/${escolaId}/materias/${mat.id}/uso`,
      );
      const detStr = formatUsoDetalhes(uso.detalhes ?? {});

      if (!mat.ativo && uso.emUso) {
        toast.error(
          detStr
            ? `Matéria inativa com vínculos (${detStr}). Remova os vínculos antes de excluir definitivamente.`
            : 'Matéria inativa ainda tem registos vinculados. Remova os vínculos antes de excluir definitivamente.',
          { duration: 6500 },
        );
        return;
      }

      if (mat.ativo && uso.emUso) {
        setDeleteConfirm({
          mat,
          title: 'Desativar matéria',
          message: detStr
            ? `Esta matéria está em uso (${detStr}). Será desativada e mantém-se na base de dados com o histórico.`
            : 'Esta matéria está em uso. Será desativada e mantém-se na base de dados com o histórico.',
          confirmLabel: 'Desativar',
          variant: 'danger',
        });
        return;
      }

      if (mat.ativo) {
        setDeleteConfirm({
          mat,
          title: 'Desativar matéria',
          message: 'Desativar esta matéria? Poderá reativá-la mais tarde.',
          confirmLabel: 'Desativar',
          variant: 'danger',
        });
        return;
      }

      setDeleteConfirm({
        mat,
        title: 'Remover matéria',
        message: 'Remover definitivamente esta matéria inativa? Esta ação não pode ser desfeita.',
        confirmLabel: 'Remover',
        variant: 'danger',
      });
    } catch {
      toast.error('Não foi possível verificar vínculos da matéria');
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { mat } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(mat.id);
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      toast.success(mat.ativo ? 'Matéria desativada' : 'Matéria removida');
    } catch (e: any) {
      const msg =
        e?.response?.data?.error?.message ??
        e?.response?.data?.message;
      toast.error(typeof msg === 'string' && msg ? msg : 'Erro ao remover matéria');
    }
  };

  const handleReativar = async (id: string) => {
    try {
      await update.mutateAsync({ id, ativo: true } as any);
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      toast.success('Matéria reativada!');
    } catch {
      toast.error('Erro ao reativar');
    }
  };

  // Filtra por área
  const materiasFiltradas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return materias.filter((m) => {
      const byQuery = !q || [m.nome, m.codigo, m.siglaBNCC, m.areaDoCOnhecimento].join(' ').toLowerCase().includes(q);
      return byQuery;
    });
  }, [materias, searchQuery]);



  const materiasAtivas = materias.filter((m) => m.ativo);
  const semMateriasAtivas = materiasAtivas.length === 0;

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar as matérias. Tente atualizar a página.');
    }
  }, [loadError]);

  const columns: ColumnDef<Materia>[] = [
    {
      accessorKey: 'nome',
      header: 'Matéria',
      cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, opacity: row.original.ativo ? 1 : 0.4 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: row.original.corVisual || '#94a3b8',
            display: 'inline-block', opacity: 0.85,
          }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.original.nome}
              </span>
              {row.original.obrigatoria && (
                <span style={{ fontSize: '0.62rem', background: 'var(--border-color, #e5e7eb)', color: 'var(--text-muted, #6b7280)', borderRadius: 4, padding: '1px 5px', fontWeight: 600, letterSpacing: '.02em' }}>
                  Obrigatória
                </span>
              )}
              {row.original.origemBNCC && (
                <span style={{ fontSize: '0.6rem', background: 'var(--border-color, #e5e7eb)', color: 'var(--text-muted, #6b7280)', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>
                  BNCC
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'professores',
      header: 'Professores',
      cell: ({ row }) => {
        const profs: { professor: { id: string; nome: string; fotoUrl?: string | null } }[] =
          (row.original as any).professorMaterias ?? [];
        if (profs.length === 0) {
          return <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>—</span>;
        }
        const MAX_SHOW = 4;
        const shown = profs.slice(0, MAX_SHOW);
        const extra = profs.length - MAX_SHOW;
        return (
          <div style={{ display: 'flex', alignItems: 'center', opacity: row.original.ativo ? 1 : 0.4 }}>
            {shown.map((pm, idx) => {
              const p = pm.professor;
              const isFotoReal = p.fotoUrl && p.fotoUrl.length > 50
                && !p.fotoUrl.includes('pravatar') && !p.fotoUrl.includes('ui-avatars');
              const commonStyle: React.CSSProperties = {
                width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--bg-card, #fff)',
                marginLeft: idx === 0 ? 0 : -10, position: 'relative', zIndex: MAX_SHOW - idx,
                flexShrink: 0,
              };
              return isFotoReal ? (
                <img
                  key={p.id}
                  src={p.fotoUrl!}
                  alt={p.nome}
                  title={p.nome}
                  style={{ ...commonStyle, objectFit: 'cover' }}
                />
              ) : (
                <div
                  key={p.id}
                  title={p.nome}
                  style={{
                    ...commonStyle,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#dbeafe', color: '#1e3a8a', fontSize: '.7rem', fontWeight: 700,
                  }}
                >
                  {p.nome.charAt(0).toUpperCase()}
                </div>
              );
            })}
            {extra > 0 && (
              <div
                title={profs.slice(MAX_SHOW).map(pm => pm.professor.nome).join(', ')}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--bg-card, #fff)',
                  marginLeft: -10, position: 'relative', zIndex: 0, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#e0e7ff', color: '#4338ca', fontSize: '.68rem', fontWeight: 700,
                }}
              >
                +{extra}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'ativo',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`badge ${row.original.ativo ? 'badge-active' : 'badge-inactive'}`}>
          {row.original.ativo ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      id: 'registro',
      header: 'Registro',
      cell: ({ row }) => {
        const m = row.original as Materia & { criadoEm?: string; atualizadoEm?: string };
        if (!m.criadoEm) return <span className="ac-resp-registro ac-resp-registro--empty">—</span>;
        return <RegistroMeta criadoEm={m.criadoEm} atualizadoEm={m.atualizadoEm} />;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const mat = row.original;
        const temVinculos = (mat._count?.horarios ?? 0) +
          (mat._count?.chamadas ?? 0) +
          (mat._count?.professorMaterias ?? 0) > 0;
        return (
          <div className="flex items-center">
            {/* Mobile: 3 pontinhos com menu de contexto */}
            <div className="flex md:hidden">
              <RowActionMenu
                items={[
                  mat.ativo
                    ? {
                        label: 'Editar Matéria',
                        icon: <Pencil size={15} />,
                        onClick: () => handlePermissionClick(canManage, () => openEdit(mat), 'Você não tem permissão para editar matérias.'),
                        variant: !canManage ? 'disabled' : 'edit',
                      }
                    : null,
                  mat.ativo
                    ? {
                        label: temVinculos ? 'Desativar Matéria (em uso)' : 'Desativar Matéria',
                        icon: <PowerOff size={15} />,
                        onClick: () => handlePermissionClick(canManage, () => void requestDelete(mat), 'Você não tem permissão para desativar matérias.'),
                        variant: !canManage ? 'disabled' : 'danger',
                      }
                    : null,
                  !mat.ativo
                    ? {
                        label: 'Reativar Matéria',
                        icon: <RotateCcw size={15} />,
                        onClick: () => handlePermissionClick(canManage, () => handleReativar(mat.id), 'Você não tem permissão para reativar matérias.'),
                        variant: !canManage ? 'disabled' : 'success',
                      }
                    : null,
                ]}
              />
            </div>

            {/* Desktop: Ações diretas */}
            <div className="hidden md:flex items-center gap-1">
              {mat.ativo ? (
                <>
                  <button
                    className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                    title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                    onClick={() => handlePermissionClick(canManage, () => openEdit(mat), 'Você não tem permissão para editar matérias.')}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : (temVinculos ? '' : 'action-btn-danger')}`}
                    title={!canManage ? 'Permissão restrita para desativação' : (temVinculos ? 'Em uso — será desativada' : 'Desativar')}
                    style={temVinculos ? { opacity: 0.5 } : {}}
                    onClick={() => handlePermissionClick(canManage, () => void requestDelete(mat), 'Você não tem permissão para desativar matérias.')}
                  >
                    <PowerOff size={15} />
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                    title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
                    onClick={() => handlePermissionClick(canManage, () => handleReativar(mat.id), 'Você não tem permissão para reativar matérias.')}
                    style={{ color: 'var(--success-color, #10b981)' }}
                  >
                    <RotateCcw size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Hero guia: mesmo contrato que Turnos; ações na barra do card */}
      {!isLoading && (
        <SetupPageHero
          icon={BookOpen}
          etapa={3}
          titulo="Matérias / Disciplinas"
          descricao={
            semMateriasAtivas
              ? 'Cadastre na lista ou importe várias de uma vez com o Assistente BNCC.'
              : 'Edite na lista, adicione matérias ou use o Assistente BNCC para importar em lote.'
          }
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}

      <div
        className="ac-admin-data-card ac-materias-data-card"
        style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar matéria..."
          actions={
            <>
              <button
                type="button"
                className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
                onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para cadastrar matérias.')}
                title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Cadastrar nova matéria'}
                style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                <Plus size={15} /> Nova matéria
              </button>
              <button
                type="button"
                onClick={() => handlePermissionClick(canManage, () => setAssistenteOpen(true), 'Você não tem permissão para importar matérias.')}
                title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Importar matérias do catálogo BNCC'}
                className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
                style={{
                  height: 34,
                  padding: '0 12px',
                  fontSize: '.78rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 8,
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  border: 'none',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(139,92,246,0.25)',
                }}
              >
                <Sparkles size={14} aria-hidden /> Assistente BNCC
              </button>
            </>
          }
          totalRecords={materiasFiltradas.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 40, 100]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable
              columns={columns}
              data={materiasFiltradas}
              isLoading={isLoading}
              searchPlaceholder="Buscar matéria..."
              showToolbar={false}
              pageSize={pageSize}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2 sm:gap-3 p-2 sm:p-3">
              {materiasFiltradas.map((mat) => {
                const countProf = mat._count?.professorMaterias ?? 0;
                const temVinculos = (mat._count?.horarios ?? 0) + (mat._count?.chamadas ?? 0) + countProf > 0;
                return (
                  <div key={mat.id} className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 sm:p-3 bg-[var(--bg-card,white)] shadow-sm hover:shadow transition-shadow flex flex-col justify-between">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, opacity: mat.ativo ? 1 : 0.4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: mat.corVisual || '#64748b', flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div className="font-bold text-xs sm:text-sm text-[var(--text-main,#1e293b)] truncate">{mat.nome}</div>
                        </div>
                      </div>
                      <span className={`badge ${mat.ativo ? 'badge-active' : 'badge-inactive'} text-[9px] px-1.5 py-0.5 shrink-0`}>{mat.ativo ? 'Ativo' : 'Inativo'}</span>
                    </div>
                    {(() => {
                      const profs: { professor: { id: string; nome: string; fotoUrl?: string | null } }[] =
                        (mat as any).professorMaterias ?? [];
                      if (profs.length === 0) return <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>Sem professores</div>;
                      const MAX_SHOW = 4;
                      const shown = profs.slice(0, MAX_SHOW);
                      const extra = profs.length - MAX_SHOW;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, gap: 6, opacity: mat.ativo ? 1 : 0.4 }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {shown.map((pm, idx) => {
                              const p = pm.professor;
                              const isFoto = p.fotoUrl && p.fotoUrl.length > 50 && !p.fotoUrl.includes('pravatar');
                              const st: React.CSSProperties = {
                                width: 24, height: 24, borderRadius: '50%', border: '2px solid var(--bg-card, #fff)',
                                marginLeft: idx === 0 ? 0 : -6, position: 'relative', zIndex: MAX_SHOW - idx, flexShrink: 0,
                              };
                              return isFoto
                                ? <img key={p.id} src={p.fotoUrl!} alt={p.nome} title={p.nome} style={{ ...st, objectFit: 'cover' as const }} />
                                : <div key={p.id} title={p.nome} style={{ ...st, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dbeafe', color: '#1e3a8a', fontSize: '.6rem', fontWeight: 700 }}>{p.nome.charAt(0).toUpperCase()}</div>;
                            })}
                            {extra > 0 && <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid var(--bg-card)', marginLeft: -6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e7ff', color: '#4338ca', fontSize: '.58rem', fontWeight: 700 }}>+{extra}</div>}
                          </div>
                          <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{profs.length} prof.</span>
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, borderTop: '1px solid var(--border-color,#e5e7eb)/50', paddingTop: 6 }}>
                      {/* Mobile: 3 pontinhos */}
                      <div className="flex md:hidden">
                        <RowActionMenu
                          items={[
                            mat.ativo && canManage
                              ? {
                                  label: 'Editar Matéria',
                                  icon: <Pencil size={15} />,
                                  onClick: () => openEdit(mat),
                                  variant: 'edit',
                                }
                              : null,
                            mat.ativo && canManage
                              ? {
                                  label: temVinculos ? 'Desativar Matéria (em uso)' : 'Desativar Matéria',
                                  icon: <PowerOff size={15} />,
                                  onClick: () => void requestDelete(mat),
                                  variant: 'danger',
                                }
                              : null,
                            !mat.ativo && canManage
                              ? {
                                  label: 'Reativar Matéria',
                                  icon: <RotateCcw size={15} />,
                                  onClick: () => handleReativar(mat.id),
                                  variant: 'success',
                                }
                              : null,
                          ]}
                        />
                      </div>

                      {/* Desktop: Ações diretas */}
                      <div className="hidden md:flex items-center gap-1">
                        {mat.ativo ? (
                          <>
                            {canManage && <button className="action-btn" title="Editar" onClick={() => openEdit(mat)}><Pencil size={13} /></button>}
                            {canManage && <button className={`action-btn ${temVinculos ? '' : 'action-btn-danger'}`} title={temVinculos ? 'Em uso — será desativada' : 'Desativar'} style={temVinculos ? { opacity: 0.5 } : { color: 'var(--danger-color, #ef4444)' }} onClick={() => void requestDelete(mat)}><PowerOff size={13} /></button>}
                          </>
                        ) : (
                          <>
                            {canManage && <button className="action-btn" title="Reativar" onClick={() => handleReativar(mat.id)} style={{ color: 'var(--success-color, #10b981)' }}><RotateCcw size={13} /></button>}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {materiasFiltradas.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: 8 }}>Nenhum registro encontrado.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={materiasFiltradas.length}
              pageSize={pageSize}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!deleteConfirm}
        title={deleteConfirm?.title ?? ''}
        message={deleteConfirm?.message ?? ''}
        confirmLabel={deleteConfirm?.confirmLabel ?? 'Confirmar'}
        variant={deleteConfirm?.variant ?? 'danger'}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDelete()}
      />

      {/* Modal Criar/Editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar matéria' : 'Nova matéria'}
        size={editing ? 'wide' : undefined}
        contentClassName={editing ? 'ac-materia-modal ac-materia-modal--edit' : 'ac-materia-modal'}
        dialogId="modal-nova-materia"
        footer={
          <>
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-save"
              onClick={() => void handleSave()}
              disabled={create.isPending || update.isPending}
            >
              {(create.isPending || update.isPending) ? 'Salvando...' : 'Salvar'}
            </button>
          </>
        }
      >
        <MateriasFormModal form={form} setForm={setForm} editing={editing} />
      </Modal>

      {/* Assistente BNCC */}
      <AssistenteBNCC
        open={assistenteOpen}
        onClose={() => setAssistenteOpen(false)}
        materiasExistentes={materias.map((m) => m.nome)}
        seriesDisponíveis={seriesDisponíveis}
        professores={professores}
      />
    </div>
  );
}
